// Create a new component: HotelAIFollowup.tsx
import React, { useState } from 'react';
import {
  Box, TextField, Button, Typography, Paper, CircularProgress, Divider
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { api } from '@services';

interface HotelAIFollowupProps {
  hotelId: string;
  analysisId: string;
}

const HotelAIFollowup: React.FC<HotelAIFollowupProps> = ({ hotelId, analysisId }) => {
  const [question, setQuestion] = useState('');
  const [answers, setAnswers] = useState<{question: string, answer: string}[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await api.post(`/hotels/${hotelId}/ai-analysis/followup`, {
        question,
        analysisId
      });

      setAnswers([...answers, {
        question,
        answer: response.data.answer
      }]);
      setQuestion('');
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to get answer');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Ask Follow-up Questions
      </Typography>

      {/* Display previous Q&A */}
      {answers.length > 0 && (
        <Paper sx={{ p: 2, mb: 3, maxHeight: 300, overflow: 'auto' }}>
          {answers.map((item, index) => (
            <Box key={index} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="primary">
                Q: {item.question}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
                {item.answer}
              </Typography>
              {index < answers.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}
        </Paper>
      )}

      {/* Question input form */}
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Ask a question about this analysis"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !question.trim()}
            endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
          >
            Ask
          </Button>
        </Box>
      </form>

      {error && (
        <Typography color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default HotelAIFollowup;